/**
 * Global Error Handler Plugin
 *
 * Catches and suppresses non-critical errors:
 * - PDF.js canvas errors from flipbook
 * - Three.js property configuration errors from 3D rendering
 * - Intentional control flow errors (POPUP_CLOSING)
 * - Audio permission errors
 */

const CHUNK_RELOAD_KEY = "nuxt_chunk_reload_attempted";

function isChunkLoadError(error: unknown): boolean {
  const msg =
    error instanceof Error ? error.message : String(error ?? "");
  return msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module");
}

function handleChunkLoadError() {
  const alreadyAttempted = sessionStorage.getItem(CHUNK_RELOAD_KEY);
  if (!alreadyAttempted) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    window.location.reload();
  } else {
    // Already reloaded once — clear the flag so future navigations can retry
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  if (typeof window === "undefined") return;

  // Clear the reload flag on successful page load
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);

  // Vue-level error handler (catches AsyncComponentWrapper failures)
  const existingVueHandler = nuxtApp.vueApp.config.errorHandler;
  nuxtApp.vueApp.config.errorHandler = (error, instance, info) => {
    if (isChunkLoadError(error)) {
      handleChunkLoadError();
      return;
    }
    if (existingVueHandler) {
      existingVueHandler(error, instance, info);
    }
  };

  const isDev = process.env.NODE_ENV === "development";

  // Store original error handler
  const originalErrorHandler = window.onerror;

  // Global error handler to catch non-critical errors
  window.onerror = function (message, source, lineno, colno, error) {
    // Check if it's an error we should suppress
    const isSuppressedError =
      source?.includes("/flipbook/") ||
      source?.includes("pdf.min.js") ||
      source?.includes("three.min.js") ||
      source?.includes("3dflipbook") ||
      source?.includes("auth-success") ||
      error?.message?.includes("InvalidStateError") ||
      error?.message?.includes("NotAllowedError") ||
      error?.message?.includes("InvalidPDFException") ||
      error?.message?.includes("Invalid PDF structure") ||
      error?.message?.includes("unconfigurable property") ||
      error?.message?.includes("THREE.WebGLRenderTarget") ||
      error?.message?.includes("POPUP_CLOSING") ||
      error?.message?.includes("canvas") ||
      error?.message?.includes("transparentCanvas") ||
      message?.toString().includes("InvalidStateError") ||
      message?.toString().includes("NotAllowedError") ||
      message?.toString().includes("InvalidPDFException") ||
      message?.toString().includes("Invalid PDF structure") ||
      message?.toString().includes("unconfigurable property") ||
      message?.toString().includes("THREE.WebGLRenderTarget") ||
      message?.toString().includes("POPUP_CLOSING");

    if (isSuppressedError) {
      // Log in dev but don't propagate the error
      if (isDev) {
        console.warn("[ErrorHandler] Caught non-critical error (suppressed):", {
          message,
          source,
          error: error?.message,
        });
      }
      // Prevent error from propagating
      return true;
    }

    // Call original handler for other errors
    if (originalErrorHandler) {
      return originalErrorHandler(message, source, lineno, colno, error);
    }

    return false;
  };

  // Also handle unhandled promise rejections from flipbook
  const originalUnhandledRejection = window.onunhandledrejection;

  window.onunhandledrejection = function (event) {
    const error = event.reason;

    if (isChunkLoadError(error)) {
      event.preventDefault();
      handleChunkLoadError();
      return;
    }

    const isSuppressedError =
      error?.message?.includes("InvalidStateError") ||
      error?.message?.includes("NotAllowedError") ||
      error?.message?.includes("InvalidPDFException") ||
      error?.message?.includes("Invalid PDF structure") ||
      error?.message?.includes("unconfigurable property") ||
      error?.message?.includes("THREE.WebGLRenderTarget") ||
      error?.message?.includes("POPUP_CLOSING") ||
      error?.message?.includes("canvas") ||
      error?.message?.includes("transparentCanvas") ||
      error?.stack?.includes("/flipbook/") ||
      error?.stack?.includes("pdf.min.js") ||
      error?.stack?.includes("three.min.js") ||
      error?.stack?.includes("3dflipbook") ||
      error?.stack?.includes("auth-success");

    if (isSuppressedError) {
      if (isDev) {
        console.warn(
          "[ErrorHandler] Caught unhandled rejection (suppressed):",
          error,
        );
      }
      event.preventDefault();
      return;
    }

    // Call original handler
    if (originalUnhandledRejection) {
      return originalUnhandledRejection.call(window, event);
    }
  };
});
